import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../Universal/firebase';
import Navbar from '../../../Universal/Navbar';
import { Check, X, MessageSquareMore } from 'lucide-react';
import { GlassContainer } from '../../../../styles';
import ResponsiveText from './ResponsiveText';
import {
  mergeAmcqGradeQuestions,
  choiceTextForLetter,
  explanationForLetter,
  submittedAtToDate,
} from '../../../../utils/amcqGradeDisplay';

const getLetterGrade = (pct) => {
  const percentage = parseInt(String(pct), 10);
  if (Number.isNaN(percentage)) return '—';
  if (percentage >= 90) return 'A';
  if (percentage >= 80) return 'B';
  if (percentage >= 70) return 'C';
  if (percentage >= 60) return 'D';
  return 'F';
};

const getGradeColors = (grade) => {
  if (grade === undefined || grade === null || grade === 0) return { color: '#858585', variant: 'clear' };
  if (grade < 60) return { color: '#c63e3e', variant: 'red' };
  if (grade < 70) return { color: '#ff8800', variant: 'orange' };
  if (grade < 80) return { color: '#ffc300', variant: 'yellow' };
  if (grade < 90) return { color: '#29c60f', variant: 'green' };
  if (grade < 100) return { color: '#006400', variant: 'darkgreen' };
  return { color: '#f198ff', variant: 'pink' };
};

/**
 * @param {{ userType: 'teacher' | 'student' }} props
 */
export default function AmcqResultsView({ userType }) {
  const { assignmentId, studentUid, classId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [rows, setRows] = useState([]);
  const [fetchStatus, setFetchStatus] = useState('loading');
  const [studentName, setStudentName] = useState('');
  const [assignmentName, setAssignmentName] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const questionRefs = useRef([]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (userType === 'student' && auth.currentUser && auth.currentUser.uid !== studentUid) {
      navigate(`/studentassignments/${classId}/completed`);
      return;
    }
    const run = async () => {
      setFetchStatus('loading');
      try {
        const gradeRef = doc(db, 'grades', `${assignmentId}_${studentUid}`);
        const snap = await getDoc(gradeRef);
        if (!snap.exists()) {
          setResults(null);
          setRows([]);
          setFetchStatus('missing');
          return;
        }
        const data = snap.data();
        setResults(data);
        setAssignmentName(data.assignmentName || '');
        setStudentName(`${data.firstName || ''} ${data.lastName || ''}`.trim());
        setRows(mergeAmcqGradeQuestions(data));
        setFetchStatus('ready');
      } catch (e) {
        console.error(e);
        setResults(null);
        setRows([]);
        setFetchStatus('error');
      }
    };
    run();
  }, [assignmentId, studentUid, userType, navigate, classId]);

  const scrollToQuestion = (index) => {
    questionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const pct = results && typeof results.SquareScore === 'number' && !Number.isNaN(results.SquareScore)
    ? Math.round(results.SquareScore)
    : 0;
  const letter = getLetterGrade(pct);
  const gradeStyle = getGradeColors(pct);
  const submitted = results ? submittedAtToDate(results.submittedAt) : null;

  if (fetchStatus === 'loading') {
    return (
      <div style={{ marginLeft: '200px', padding: '40px', fontFamily: "'montserrat', sans-serif" }}>
        Loading…
      </div>
    );
  }

  if (fetchStatus === 'error') {
    return (
      <div style={{ marginLeft: '200px', padding: '40px', fontFamily: "'montserrat', sans-serif" }}>
        Could not load results. Please try again.
      </div>
    );
  }

  if (fetchStatus === 'missing' || !results || rows.length === 0) {
    return (
      <div style={{ marginLeft: '200px', padding: '40px', fontFamily: "'montserrat', sans-serif" }}>
        No submission found for this assignment.
      </div>
    );
  }

  const navItems = [
    { type: 'assignmentName', id: assignmentId, label: assignmentName || 'Assignment' },
    { type: 'studentGrades', id: studentUid, label: studentName || 'Student' },
  ];

  const gradesPath = `/class/${classId}/student/${studentUid}/grades`;
  const teacherAssignmentPath = `/class/${classId}/assignment/${assignmentId}/TeacherResultsAMCQ`;
  const studentCompletedPath = `/studentassignments/${classId}/completed`;

  return (
    <div
      style={{
        minHeight: '100vh',
        width: 'calc(100% - 200px)',
        marginLeft: '200px',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <Navbar userType={userType} navItems={navItems} classId={classId} />

      <div
        style={{
          fontFamily: "'montserrat', sans-serif",
          width: '100%',
          zIndex: 20,
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: '0px',
        }}
      >
        <div style={{ display: 'flex' }}>
          <div
            style={{
              marginBottom: '30px',
              minHeight: '80px',
              marginLeft: '4%',
              alignItems: 'center',
              display: 'flex',
              width: '92%',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <h1
              role="button"
              tabIndex={0}
              onClick={() => navigate(userType === 'teacher' ? gradesPath : studentCompletedPath)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') navigate(userType === 'teacher' ? gradesPath : studentCompletedPath);
              }}
              style={{
                fontSize: '1.3rem',
                color: 'black',
                fontFamily: "'montserrat', sans-serif",
                textAlign: 'left',
                fontWeight: '400',
                cursor: 'pointer',
                margin: 0,
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
              }}
            >
              {studentName}
            </h1>

            <GlassContainer
              variant={gradeStyle.variant}
              size={0}
              style={{ zIndex: 25, position: 'relative' }}
              contentStyle={{
                padding: '2px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ fontSize: '.8rem', color: gradeStyle.color, fontWeight: '500' }}>{letter}</span>
                <span
                  style={{
                    width: '1px',
                    marginTop: '3px',
                    background: gradeStyle.color,
                    height: '10px',
                  }}
                />
                <span style={{ fontSize: '.8rem', color: gradeStyle.color, fontWeight: '500' }}>{pct}%</span>
              </div>
            </GlassContainer>

            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <h1
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (userType === 'teacher') navigate(teacherAssignmentPath);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && userType === 'teacher') navigate(teacherAssignmentPath);
                }}
                style={{
                  fontSize: '.9rem',
                  color: 'grey',
                  marginTop: '4px',
                  fontWeight: '500',
                  cursor: userType === 'teacher' ? 'pointer' : 'default',
                  margin: 0,
                }}
                onMouseEnter={(e) => {
                  if (userType === 'teacher') e.target.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.target.style.textDecoration = 'none';
                }}
              >
                {assignmentName}
                <span
                  style={{
                    fontSize: '.9rem',
                    fontWeight: '600',
                    color: '#7D00EA',
                    marginLeft: '6px',
                  }}
                >
                  MC<span style={{ color: '#FFAE00' }}>*</span>
                </span>
              </h1>
              <div style={{ height: '20px', width: '1px', background: '#EDEDED', margin: '3px 6px' }} />
              <h1 style={{ fontSize: '12px', color: 'grey', marginTop: '4px', fontWeight: '400', margin: 0 }}>
                {submitted ? submitted.toLocaleString() : ''}
              </h1>
            </div>
          </div>
        </div>

        <div
          style={{
            position: 'sticky',
            height: '40px',
            top: '0px',
            left: '200px',
            width: '100%',
            background: 'rgba(255,255,255,.9)',
            backdropFilter: 'blur(5px)',
            borderBottom: isScrolled ? '1px solid #ddd' : '1px solid transparent',
            marginTop: '-12px',
            transition: 'all 0.3s',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              marginLeft: 'calc(4% - 10px)',
              width: '86%',
              display: 'flex',
              overflowX: 'auto',
              marginTop: '4px',
              flex: 1,
            }}
          >
            {rows.map((q, index) => (
              <div
                key={q.questionId || index}
                onClick={() => scrollToQuestion(index)}
                style={{
                  minWidth: '28px',
                  cursor: 'pointer',
                  padding: '8px 2px',
                }}
              >
                {q.isCorrect ? <Check size={16} color="#16a34a" /> : <X size={16} color="#dc2626" />}
              </div>
            ))}
          </div>
          <div
            style={{
              marginRight: '4%',
              marginLeft: 'auto',
              marginTop: '4px',
              opacity: isScrolled ? 1 : 0,
              visibility: isScrolled ? 'visible' : 'hidden',
              transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            }}
          >
            <GlassContainer
              variant={gradeStyle.variant}
              size={0}
              style={{ zIndex: 25, position: 'relative' }}
              contentStyle={{
                display: 'flex',
                padding: '2px 10px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontSize: '.9rem', color: gradeStyle.color, fontWeight: '500' }}>{pct}%</span>
            </GlassContainer>
          </div>
        </div>

        <ul style={{ listStyle: 'none', padding: 0, marginTop: '10px', width: '100%', marginLeft: 'auto', marginRight: 'auto' }}>
          {rows.map((q, index) => {
            const sel = String(q.selectedChoice || '').toLowerCase();
            const cor = String(q.correctChoice || '').toLowerCase();
            const yourText = choiceTextForLetter(q.choices, sel) || '—';
            const correctText = choiceTextForLetter(q.choices, cor) || '—';
            const explYour = (explanationForLetter(q.explanations, sel) || '').trim();
            const explCorrect = (explanationForLetter(q.explanations, cor) || '').trim();
            const mergedCorrectExpl =
              explYour && explCorrect && explYour === explCorrect
                ? explYour
                : explCorrect || explYour;

            const explBlock = (body) => (
              <div style={{ marginTop: '12px', width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <MessageSquareMore size={16} strokeWidth={1.5} style={{ marginRight: '10px', color: '#9ca3af' }} />
                  <span style={{ fontSize: '13px', color: '#555', fontWeight: '500' }}>Explanation</span>
                </div>
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: '1px solid #e8e8e8',
                    background: 'rgba(249, 250, 251, 0.95)',
                  }}
                >
                  <p style={{ fontSize: '0.9rem', color: '#444', margin: 0, lineHeight: 1.55 }}>
                    {body || 'No explanation stored for this choice.'}
                  </p>
                </div>
              </div>
            );

            return (
              <li
                key={q.questionId || index}
                ref={(el) => {
                  questionRefs.current[index] = el;
                }}
                style={{
                  position: 'relative',
                  isolation: 'isolate',
                  fontFamily: "'montserrat', sans-serif",
                  marginBottom: '20px',
                  background: 'white',
                  width: '92%',
                  padding: '0% 2%',
                  margin: '16px 2%',
                  borderBottom: '1px solid lightgrey',
                  paddingBottom: '24px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '12px' }}>
                  <GlassContainer
                    variant={q.isCorrect ? 'green' : 'red'}
                    size={0}
                    style={{ zIndex: 30, position: 'relative', flexShrink: 0 }}
                    contentStyle={{
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {q.isCorrect ? <Check size={16} color="#16a34a" /> : <X size={16} color="#dc2626" />}
                  </GlassContainer>

                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '100%' }}>
                      <ResponsiveText text={q.question || ''} maxFontSize={20} minFontSize={14} />
                    </div>
                  </div>
                </div>

                <div style={{ width: '100%', marginTop: '12px' }}>
                    {q.sectorNumber != null ? (
                      <div style={{ marginTop: '0px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <GlassContainer variant="clear" size={0} style={{ zIndex: 30, position: 'relative' }} contentStyle={{ padding: '6px 12px' }}>
                          <span style={{ fontSize: '12px', color: '#888' }}>Module {q.sectorNumber}</span>
                        </GlassContainer>
                      </div>
                    ) : null}

                    {q.isCorrect ? (
                      <div style={{ marginTop: '18px', width: '100%' }}>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#555',
                            fontWeight: '600',
                            marginBottom: '8px',
                            letterSpacing: '0.02em',
                          }}
                        >
                          Your answer
                        </div>
                        <div style={{ width: '100%', maxWidth: '100%' }}>
                          <GlassContainer
                            variant="green"
                            size={0}
                            style={{ width: '100%', maxWidth: '100%', display: 'block', zIndex: 30, position: 'relative' }}
                            contentStyle={{
                              padding: '12px 16px',
                              width: '100%',
                              boxSizing: 'border-box',
                            }}
                          >
                            <span style={{ fontSize: '15px', color: '#166534', fontWeight: '500', lineHeight: 1.45 }}>
                              {correctText}
                            </span>
                          </GlassContainer>
                        </div>
                        {explBlock(mergedCorrectExpl)}
                      </div>
                    ) : (
                      <div style={{ marginTop: '18px', width: '100%' }}>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#555',
                            fontWeight: '600',
                            marginBottom: '8px',
                            letterSpacing: '0.02em',
                          }}
                        >
                          Your answer
                        </div>
                        <div style={{ width: '100%', maxWidth: '100%' }}>
                          <GlassContainer
                            variant="red"
                            size={0}
                            style={{ width: '100%', maxWidth: '100%', display: 'block', zIndex: 30, position: 'relative' }}
                            contentStyle={{
                              padding: '12px 16px',
                              width: '100%',
                              boxSizing: 'border-box',
                            }}
                          >
                            <span style={{ fontSize: '15px', color: '#991b1b', fontWeight: '500', lineHeight: 1.45 }}>
                              {yourText}
                            </span>
                          </GlassContainer>
                        </div>
                        {explBlock(explYour)}

                        <div
                          style={{
                            fontSize: '12px',
                            color: '#555',
                            fontWeight: '600',
                            marginBottom: '8px',
                            marginTop: '22px',
                            letterSpacing: '0.02em',
                          }}
                        >
                          Correct answer
                        </div>
                        <div style={{ width: '100%', maxWidth: '100%' }}>
                          <GlassContainer
                            variant="green"
                            size={0}
                            style={{ width: '100%', maxWidth: '100%', display: 'block', zIndex: 30, position: 'relative' }}
                            contentStyle={{
                              padding: '12px 16px',
                              width: '100%',
                              boxSizing: 'border-box',
                            }}
                          >
                            <span style={{ fontSize: '15px', color: '#166534', fontWeight: '500', lineHeight: 1.45 }}>
                              {correctText}
                            </span>
                          </GlassContainer>
                        </div>
                        {explBlock(explCorrect)}
                      </div>
                    )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
